import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CheckCircle2, AlertCircle, Circle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOT_COLOR = {
  ok:      'bg-emerald-500',
  partial: 'bg-amber-500',
  unset:   'bg-slate-300 dark:bg-slate-600',
};

/**
 * Small health tile listing every optional integration with green/amber/grey
 * status dots so admins can spot missing configuration at a glance.
 * Rendered on the AdminDashboard component in DashboardPage.
 */
const IntegrationsHealthWidget = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/integrations/health`, { withCredentials: true });
      setItems(r.data.integrations || []);
    } catch {
      // Non-admin/hr — widget just doesn't render its list.
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading || items.length === 0) return null;

  return (
    <Card data-testid="integrations-health-widget" className="border-l-4 border-l-indigo-500">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Integration health</CardTitle>
          <button
            onClick={() => navigate('/admin/integrations')}
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            data-testid="health-widget-configure-link"
          >
            Configure <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {items.map(it => (
          <div key={it.integration} className="flex items-center justify-between text-sm" data-testid={`health-row-${it.integration}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${DOT_COLOR[it.state] || DOT_COLOR.unset}`} />
              <span className="truncate">{it.label}</span>
            </div>
            <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-1">
              {it.state === 'ok' ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : it.state === 'partial' ? <AlertCircle className="w-3 h-3 text-amber-500" /> : <Circle className="w-3 h-3" />}
              {it.keys_set}/{it.keys_total}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default IntegrationsHealthWidget;
